import { useState, useCallback } from 'react';
import { defaultGridListPageSize, pageSizeOptions } from '../util/Constants';

const PAGE_SIZE_KEY_PREFIX = 'pageSize_';

/**
 * Custom hook that persists the grid page size selection to localStorage.
 * @param entityName - Unique name for the entity/page (e.g., 'errorLogs', 'eventLogs', 'documentArchives')
 * @param customOptions - Optional custom array of allowed page sizes. Falls back to pageSizeOptions from Constants.
 * @returns [pageSize, handlePageSizeChange] - current page size and setter that also persists to localStorage
 */
export const usePageSize = (entityName: string, customOptions?: number[]): [number, (newSize: number) => void] => {
    const storageKey = PAGE_SIZE_KEY_PREFIX + entityName;
    const allowedOptions = customOptions ?? pageSizeOptions;

    const [pageSize, setPageSize] = useState<number>(() => {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
            const parsed = Number(stored);
            if (allowedOptions.includes(parsed)) {
                return parsed;
            }
        }
        // When customOptions are provided, default to first custom option
        // in case defaultGridListPageSize isn't in the allowed list
        return customOptions ? customOptions[0] : defaultGridListPageSize;
    });

    const handlePageSizeChange = useCallback((newSize: number) => {
        localStorage.setItem(storageKey, String(newSize));
        setPageSize(newSize);
    }, [storageKey]);

    return [pageSize, handlePageSizeChange];
};